import {
  Breakpoints,
  BreakpointWrapper,
  Container,
  ContainerGutter,
  Text,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useState } from 'react';
import styled from 'styled-components';

const DescriptionText = styled(Text)`
  font-weight: 700;
  font-style: Bold;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
`;

const SecondaryText = styled(Text)`
  font-weight: 300;
  font-style: Light;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
`;

const ImagesWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImagesContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(15px, 5vw, 120px);
  margin-top: clamp(20px, 3vh, 50px);
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const Image = styled.div<{
  imageUrl?: string;
  correctAnswer: boolean;
  selected?: boolean;
  isCorrectAnswered?: boolean;
  isDisabled?: boolean;
  hasSelection?: boolean;
}>`
  width: clamp(95px, 15vw, 350px);
  height: clamp(95px, 15vw, 350px);
  background-color: #c90010;
  background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : 'none')};
  background-size: cover;
  background-position: center;
  cursor: ${({ isCorrectAnswered, isDisabled }) => (isCorrectAnswered || isDisabled ? 'not-allowed' : 'pointer')};
  border: ${({ selected, isCorrectAnswered, correctAnswer }) =>
    selected || (isCorrectAnswered && correctAnswer) ? '3px solid #ffffff' : 'none'};
  opacity: ${({ isCorrectAnswered, correctAnswer, isDisabled }) =>
    isDisabled || (isCorrectAnswered && !correctAnswer) ? 0.4 : 1};
  transition:
    border 0.3s ease,
    opacity 0.3s ease;
`;

const DesktopFeedbackContainer = styled.div`
  position: absolute;
  top: calc(100% + clamp(35px, 2vh, 40px));
  left: 50%;
  transform: translateX(-50%);
  height: clamp(60px, 8vh, 140px);
  width: clamp(180px, 30vw, 600px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const MobileFeedbackContainer = styled.div`
  margin-top: clamp(15px, 2vh, 40px);
  height: clamp(60px, 8vh, 140px);
  width: 90vw;
  max-width: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  margin-left: auto;
  margin-right: auto;
`;

const FeedbackText = styled(Text)`
  weight: 700;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
  padding: 0 clamp(5px, 1vw, 25px);
  font-size: clamp(14px, 1.4vw, 16px);
`;

const Question1Wrapper = styled.div`
  margin-top: -10vh;
`;

interface Question1Props {
  onAnswerSelected: (isCorrect: boolean) => void;
  handleIncorrect: () => void;
}

const images = [
  {
    imageUrl:
      'https://assets.volkswagen.com/is/image/volkswagenag/carroceria01?Zml0PWNyb3AlMkMxJndpZD0xMDgxJmhlaT0xMDgxJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJjBiM2U=',
    correctAnswer: true,
    feedbackText: (
      <>
        <strong>¡Correcto!</strong> El Rojo Tornado es el color que usamos en nuestros modelos de alto rendimiento desde
        1986.
      </>
    ),
  },
  {
    imageUrl:
      'https://assets.volkswagen.com/is/image/volkswagenag/carroceria02?Zml0PWNyb3AlMkMxJndpZD0xMDgxJmhlaT0xMDgxJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJjBiM2U=',
    correctAnswer: false,
    feedbackText: (
      <>
        <strong>¡Casi lo tienes!</strong> <br /> Prueba con otra pintura.
      </>
    ),
  },
  {
    imageUrl:
      'https://assets.volkswagen.com/is/image/volkswagenag/carroceria03?Zml0PWNyb3AlMkMxJndpZD0xMDgxJmhlaT0xMDgxJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJjBiM2U=',
    correctAnswer: false,
    feedbackText: (
      <>
        <strong>¡Casi lo tienes!</strong> <br /> Prueba con otra pintura.
      </>
    ),
  },
];

export const Question1 = ({ onAnswerSelected, handleIncorrect }: Question1Props): React.ReactElement => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrectAnswered, setIsCorrectAnswered] = useState(false);
  const [correctIndex, setCorrectIndex] = useState<number | null>(null);
  const [blockedImages, setBlockedImages] = useState<boolean[]>([false, false, false]);
  const feedbackTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleImageClick = (index: number) => {
    if (isCorrectAnswered || blockedImages[index]) return;

    //Limpiamos el timeout antiguo
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }

    setSelectedImage(index);
    setShowFeedback(true);

    const isCorrect = images[index].correctAnswer;
    onAnswerSelected(isCorrect);

    if (!isCorrect) {
      handleIncorrect();
      setBlockedImages((prev) => {
        const newStates = [...prev];
        newStates[index] = true;
        return newStates;
      });
      feedbackTimeoutRef.current = setTimeout(() => {
        setSelectedImage(null);
        setShowFeedback(false);
      }, 4000);
    } else {
      setIsCorrectAnswered(true);
      setCorrectIndex(index);
    }
  };
  return (
    <Question1Wrapper>
      <Container horizontalAlign={'center'} wrap={'always'} gutter={ContainerGutter.static400}>
        <Container padding={{ left: ContainerGutter.grid002, right: ContainerGutter.grid002 }}>
          <DescriptionText appearance={TokenTextAppearance.headline200}>
            ¿Eres capaz de reconocer el clásico Rojo Tornado de nuestros coches de alto rendimiento?
          </DescriptionText>
        </Container>

        <SecondaryText appearance={TokenTextAppearance.headline200}>Selecciona la opción correcta.</SecondaryText>

        <ImagesWrapper>
          <ImagesContainer>
            {images.map((image, index) => (
              <ImageWrapper key={index}>
                <Image
                  imageUrl={image.imageUrl}
                  correctAnswer={image.correctAnswer}
                  selected={selectedImage === index}
                  isCorrectAnswered={isCorrectAnswered}
                  isDisabled={blockedImages[index]}
                  hasSelection={selectedImage !== null}
                  onClick={() => handleImageClick(index)}
                />
                <BreakpointWrapper min={Breakpoints.b960}>
                  <DesktopFeedbackContainer>
                    {((showFeedback && selectedImage === index) || (isCorrectAnswered && correctIndex === index)) && (
                      <FeedbackText appearance={TokenTextAppearance.headline100}>{image.feedbackText}</FeedbackText>
                    )}
                  </DesktopFeedbackContainer>
                </BreakpointWrapper>
              </ImageWrapper>
            ))}
          </ImagesContainer>

          <BreakpointWrapper max={Breakpoints.b960}>
            <MobileFeedbackContainer>
              {((showFeedback && selectedImage !== null) || (isCorrectAnswered && correctIndex !== null)) && (
                <FeedbackText appearance={TokenTextAppearance.headline100}>
                  {images[isCorrectAnswered && correctIndex !== null ? correctIndex : selectedImage!].feedbackText}
                </FeedbackText>
              )}
            </MobileFeedbackContainer>
          </BreakpointWrapper>
        </ImagesWrapper>
      </Container>
    </Question1Wrapper>
  );
};
